import React, { useEffect, useState, useCallback } from 'react'
import { Typography, Pagination, Row, Modal, Tooltip, Spin } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import styles from './Users.module.less'
import { UserItem } from './UserItem'
import { getLeftSiderCollapsed } from '~/Store/Layout/selectors'
import { UserFilter } from './UserFilter'
import { OrganizationsService } from '~/Services/OrganizationService'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { UserItemData } from '~/Services/UserService.d'
import { getPagination, GlobalActions } from '~/Store/Global'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import {
  getModalVisibility,
  getOrganizationListUsersRefetch,
  getOrgUserCurrentPage,
  getUserFilterState,
} from '~/Store/Organization/selectors'
import { OrganizationActions, OrgModelName } from '~/Store/Organization'
import { errorNotification } from '~/Services'
import { WaitOverlay } from '~/Components'
import { WaitOverlayActions } from '~/Store/Overlays'
import { NoData } from '~/Components/NoData'
import { ScrollBar } from '~/Components/ScrollBar'
import useHasPermissions from '~/Hooks/useHasPermissions'
import { OrgClaim, RootClaim } from '../../Routes'
import { CreateUser, UserInformation } from './CreateUser'
import { UserProfileObject } from '~/Services/organization.types'
import { InviteUser } from './InviteUser'
import { useSelector } from 'react-redux'
import { UsersActions } from '~/Store/AdminUser/slice'
import { RootState } from '~/Store/reducers'

export const Users = () => {
  const waitOverlayName = OrgModelName.fetchingOrgUsers
  const dispatch = useAppDispatch()
  const leftMenuCollapsed = useAppSelector(getLeftSiderCollapsed)
  const defaultOrg = useAppSelector(getDefaultOrganization)
  const hasAdmin = useHasPermissions([RootClaim.System, OrgClaim.Admin])
  const pagination = useAppSelector(getPagination)
  const { pageSize } = pagination
  const currentPage = useAppSelector(getOrgUserCurrentPage)
  const refetch = useAppSelector(getOrganizationListUsersRefetch)
  const fetchingUsers = useAppSelector(
    getModalVisibility(OrgModelName.fetchingOrgUsers),
  )
  const filterState = useAppSelector(getUserFilterState)

  const users: UserItemData[] = useSelector(
    (state: RootState) => state.users.listUsersData?.results || [],
  )

  const [totalItem, setTotalItem] = useState(1)

  const isModalVisible = useAppSelector(
    getModalVisibility(OrgModelName.newUserModal),
  )

  const fetchOrganizationUsers = useCallback(async () => {
    if (!defaultOrg) return
    try {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }),
      )

      let statusFilter = ''
      let queryString = ''

      if (filterState.criteria) {
        queryString += `(contains(userName, '${filterState.criteria}')`
        queryString += ` or contains(email, '${filterState.criteria}')`
        queryString += ` or contains(phoneNumber, '${filterState.criteria}')`
        queryString += ` or contains(firstname, '${filterState.criteria}')`
        queryString += ` or contains(lastname, '${filterState.criteria}'))`
      }

      const res: any = await OrganizationsService.Get.getOrganizationUsers(
        defaultOrg.guid,
        {
          filter:
            [statusFilter, queryString].filter(x => x).join(' and ') ||
            undefined,
          expand: 'Licenses',
          page: currentPage,
          pageSize,
        },
        filterState.userType.length > 0 ? filterState.userType : undefined,
        filterState.status ? [filterState.status] : undefined,
      )

      const { data } = res
      const { page, queryCount } = data

      dispatch(UsersActions.setUsersSuccess(data))
      dispatch(OrganizationActions.setUsersCurrentPage(page))
      setTotalItem(queryCount)
    } catch (error: any) {
      errorNotification(error)
    } finally {
      dispatch(
        WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }),
      )
    }
  }, [
    defaultOrg,
    currentPage,
    pageSize,
    filterState,
    dispatch,
    waitOverlayName,
  ])

  // Refetch on dependency changes
  useEffect(() => {
    fetchOrganizationUsers()
  }, [fetchOrganizationUsers, refetch])

  const changePage = (_page: number, _pageSize?: number | undefined) => {
    dispatch(OrganizationActions.setUsersCurrentPage(_page))
    dispatch(
      GlobalActions.setPagination({ ...pagination, pageSize: _pageSize }),
    )
  }

  const handleOk = (value: UserInformation) => {
    const userProfile: UserProfileObject = {
      userName: value.email,
      email: value.email,
      firstname: value.firstName,
      lastname: value.lastName,
      password: value.password,
      forcePasswordChange: false,
      gender: '',
    }
    dispatch(OrganizationActions.registerUserByAdminOrgRequest(userProfile))
  }

  const handleCancel = () => {
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.newUserModal,
        value: false,
      }),
    )
  }

  const showInviteUserModal = () => {
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.inviteUserVisibility,
        value: true,
      }),
    )
  }

  return (
    <WaitOverlay
      name={waitOverlayName}
      size="large"
      wrapperClassName={styles.FullOverlay}
    >
      <InviteUser />
      <Modal
        title="Create user"
        visible={isModalVisible}
        okButtonProps={{ disabled: true, hidden: true }}
        cancelButtonProps={{ disabled: true, hidden: true }}
        onCancel={handleCancel}
        footer={null}
      >
        <CreateUser onSubmit={handleOk} />
      </Modal>

      <div className={styles.OrganizationUsers}>
        <Row align="stretch">
          <Typography.Title style={{ flex: 1 }} level={4}>
            Organization Users
          </Typography.Title>

          <Tooltip title="Invite User">
            <UserAddOutlined
              className={styles.AddUser}
              onClick={showInviteUserModal}
              style={{ color: 'blue', marginBottom: '40px' }}
            />
          </Tooltip>

          <UserFilter />
        </Row>

        <Spin spinning={fetchingUsers} size="large">
          <div className={styles.UserList}>
            <ScrollBar style={{ paddingRight: '16px', marginRight: '-16px' }}>
              {users.length > 0 ? (
                users.map((item: UserItemData) => (
                  <UserItem
                    key={item.id}
                    user={item}
                    hasAdmin={hasAdmin}
                    onRefresh={fetchOrganizationUsers}
                  />
                ))
              ) : (
                <NoData content="No data" />
              )}
            </ScrollBar>
          </div>
        </Spin>

        <div
          className={styles.Pagination}
          style={{
            width: `calc(100% - ${leftMenuCollapsed ? '146' : '266'}px)`,
          }}
        >
          <Pagination
            current={currentPage}
            total={totalItem}
            pageSize={pageSize}
            showSizeChanger
            responsive
            onChange={changePage}
          />
        </div>
      </div>
    </WaitOverlay>
  )
}
